// Single source of truth for the vendor/admin password minimum. Must match the
// Supabase Auth project policy (Authentication → Policies). If they drift, the
// set-password/reset error pass-through (auth.ts) surfaces Supabase's real
// requirement to the user.
export const MIN_PASSWORD_LENGTH = 8;
export const MAX_PASSWORD_LENGTH = 72; // bcrypt limit enforced by Supabase

export type PasswordRuleId = 'length' | 'upper' | 'lower' | 'number' | 'symbol';

export type PasswordRule = {
  id: PasswordRuleId;
  label: string;
  test: (pw: string) => boolean;
};

// Order = display order. The set maps 1:1 onto Supabase Auth's strongest preset
// (lowercase, uppercase, digits and symbols) plus the minimum length, so this
// checklist and the server policy cannot drift. "Symbol" = any non-alphanumeric
// character, matching Supabase's symbol definition.
export const PASSWORD_RULES: PasswordRule[] = [
  {
    id: 'length',
    label: `At least ${MIN_PASSWORD_LENGTH} characters`,
    test: (pw) => pw.length >= MIN_PASSWORD_LENGTH,
  },
  { id: 'upper', label: 'One uppercase letter (A–Z)', test: (pw) => /[A-Z]/.test(pw) },
  { id: 'lower', label: 'One lowercase letter (a–z)', test: (pw) => /[a-z]/.test(pw) },
  { id: 'number', label: 'One number (0–9)', test: (pw) => /[0-9]/.test(pw) },
  { id: 'symbol', label: 'One symbol (!@#$…)', test: (pw) => /[^A-Za-z0-9]/.test(pw) },
];

export type PasswordRuleResult = { id: PasswordRuleId; label: string; met: boolean };

export function evaluatePassword(pw: string): PasswordRuleResult[] {
  return PASSWORD_RULES.map((rule) => ({ id: rule.id, label: rule.label, met: rule.test(pw) }));
}

export function isPasswordValid(pw: string): boolean {
  return PASSWORD_RULES.every((rule) => rule.test(pw));
}
